import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import merge from 'lodash/merge';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { changeUserEmail } from '../../util/api';
import { fetchCurrentUser, setCurrentUser } from '../../ducks/user.duck';

// ================ Async thunks ================ //

export const savePhoneNumberThunk = createAsyncThunk(
  'ContactDetailsPage/savePhoneNumber',
  ({ phoneNumber }, { dispatch, extra: sdk, rejectWithValue }) => {
    return sdk.currentUser
      .updateProfile(
        { protectedData: { phoneNumber } },
        {
          expand: true,
          include: ['profileImage'],
          'fields.image': ['variants.square-small', 'variants.square-small2x'],
        }
      )
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        dispatch(setCurrentUser(entities[0]));
        return entities[0];
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);
// Backward compatible wrapper for the requestSavePhoneNumber thunk
export const savePhoneNumber = params => dispatch => {
  return dispatch(savePhoneNumberThunk({ phoneNumber: params.phoneNumber })).unwrap();
};

export const saveEmailThunk = createAsyncThunk(
  'ContactDetailsPage/requestSaveEmail',
  ({ email }, { dispatch, rejectWithValue }) => {
    return changeUserEmail({ email })
      .then(response => {
        // Server returns { status, statusText, data } where data is the SDK API body.
        const entities = denormalisedResponseEntities({ data: response.data });
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the change-email response');
        }
        dispatch(setCurrentUser(entities[0]));
        return entities[0];
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);
// Backward compatible wrapper for the requestSaveEmail thunk
export const saveEmail = params => dispatch => {
  return dispatch(saveEmailThunk({ email: params.email })).unwrap();
};

export const saveEmailAndPhoneNumberThunk = createAsyncThunk(
  'ContactDetailsPage/saveEmailAndPhoneNumber',
  ({ email, phoneNumber }, { dispatch, rejectWithValue }) => {
    const promises = [
      dispatch(saveEmailThunk({ email })).unwrap(),
      dispatch(savePhoneNumberThunk({ phoneNumber })).unwrap(),
    ];

    return Promise.all(promises)
      .then(values => {
        const saveEmailUser = values[0];
        const savePhoneNumberUser = values[1];

        const protectedData = savePhoneNumberUser.attributes.profile.protectedData;
        const phoneNumberMergeSource = { attributes: { profile: { protectedData } } };

        const currentUser = merge(saveEmailUser, phoneNumberMergeSource);
        dispatch(setCurrentUser(currentUser));
        return currentUser;
      })
      .catch(e => {
        return rejectWithValue(e);
      });
  }
);
// Backward compatible wrapper for the saveEmailAndPhoneNumber thunk
export const saveEmailAndPhoneNumber = params => dispatch => {
  return dispatch(
    saveEmailAndPhoneNumberThunk({
      email: params.email,
      phoneNumber: params.phoneNumber,
    })
  ).unwrap();
};

export const saveContactDetailsThunk = createAsyncThunk(
  'ContactDetailsPage/saveContactDetails',
  ({ email, currentEmail, phoneNumber, currentPhoneNumber }, { dispatch, rejectWithValue }) => {
    const emailChanged = email !== currentEmail;
    const phoneNumberChanged = phoneNumber !== currentPhoneNumber;

    if (emailChanged && phoneNumberChanged) {
      return dispatch(saveEmailAndPhoneNumberThunk({ email, phoneNumber }))
        .unwrap()
        .catch(e => rejectWithValue(e));
    } else if (emailChanged) {
      return dispatch(saveEmailThunk({ email }))
        .unwrap()
        .catch(e => rejectWithValue(e));
    } else if (phoneNumberChanged) {
      return dispatch(savePhoneNumberThunk({ phoneNumber }))
        .unwrap()
        .catch(e => rejectWithValue(e));
    }
    return Promise.resolve();
  }
);
// Backward compatible wrapper for the saveContactDetails thunk
export const saveContactDetails = params => dispatch => {
  return dispatch(saveContactDetailsThunk(params)).unwrap();
};

// ================ Slice ================ //

const contactDetailsSlice = createSlice({
  name: 'ContactDetailsPage',
  initialState: {
    saveEmailError: null,
    savePhoneNumberError: null,
    saveContactDetailsInProgress: false,
    contactDetailsChanged: false,
  },
  reducers: {
    saveContactDetailsClear: state => {
      state.saveContactDetailsInProgress = false;
      state.saveEmailError = null;
      state.savePhoneNumberError = null;
      state.contactDetailsChanged = false;
    },
  },
  extraReducers: builder => {
    builder
      // Request save phone number
      .addCase(savePhoneNumberThunk.rejected, (state, action) => {
        state.saveContactDetailsInProgress = false;
        state.savePhoneNumberError = action.payload;
      })
      // Request save email
      .addCase(saveEmailThunk.rejected, (state, action) => {
        state.saveContactDetailsInProgress = false;
        state.saveEmailError = action.payload;
      })
      // Save contact details
      .addCase(saveContactDetailsThunk.pending, state => {
        state.saveContactDetailsInProgress = true;
        state.saveEmailError = null;
        state.savePhoneNumberError = null;
        state.contactDetailsChanged = false;
      })
      .addCase(saveContactDetailsThunk.fulfilled, state => {
        state.saveContactDetailsInProgress = false;
        state.contactDetailsChanged = true;
      })
      .addCase(saveContactDetailsThunk.rejected, state => {
        state.saveContactDetailsInProgress = false;
        // Error details are handled by individual thunks
      });
  },
});

export const { saveContactDetailsClear } = contactDetailsSlice.actions;
export default contactDetailsSlice.reducer;

// ================ Load data ================ //

export const loadData = () => {
  // Since verify email happens in separate tab, current user's data might be updated
  return fetchCurrentUser();
};
